import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../components/SignUpModal.css';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { updatePassword, session, loading: authLoading } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const hasRecoverySession = !!session;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await updatePassword(password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#020617] text-white font-sans flex flex-col items-center justify-center px-6">
        <p className="text-slate-400">Loading...</p>
      </div>
    );
  }

  if (!hasRecoverySession) {
    return (
      <div className="min-h-screen bg-[#020617] text-white font-sans flex flex-col items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold mb-4">Invalid or expired link</h1>
          <p className="text-slate-400 mb-6">
            This password reset link is invalid or has expired. Request a new link below.
          </p>
          <button
            onClick={() => navigate('/', { state: { showForgotPassword: true } })}
            className="px-6 py-3 rounded-lg font-semibold text-black bg-gradient-to-r from-amber-400 to-yellow-600 hover:from-amber-300 hover:to-yellow-500 transition-all"
          >
            Request new reset link
          </button>
          <button
            onClick={() => navigate('/')}
            className="mt-4 block w-full py-2 text-slate-400 hover:text-white transition-colors"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#020617] text-white font-sans flex flex-col items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold mb-4">Password updated</h1>
          <p className="text-slate-400 mb-6">
            Your password has been reset. You can now log in with your new password.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-lg font-semibold text-black bg-gradient-to-r from-amber-400 to-yellow-600 hover:from-amber-300 hover:to-yellow-500 transition-all"
          >
            Go to Log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans flex flex-col items-center justify-center px-6">
      <div className="max-w-md w-full signup-modal">
        <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-8">
          <h1 className="text-xl font-bold mb-2">Set new password</h1>
          <p className="text-slate-400 text-sm mb-6">
            Enter your new password below.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ padding: 0 }}>
              <label className="block mb-4">
                New password
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>
              <label className="block mb-4">
                Confirm password
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </label>
            </div>
            {error && (
              <p className="modal-error mb-4" role="alert">
                {error}
              </p>
            )}
            <div className="modal-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Updating...' : 'Update password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
